import { useMemo } from 'react';
import { format, isSameDay, isAfter } from 'date-fns';
import { Clock, Calendar } from 'lucide-react';
import { Appointment } from '../api';
import { toSalonTime, salonNow } from '../lib/datetime';
import { STATUS_DOT } from '../lib/statusColors';

interface TodayAgendaProps {
  appointments: Appointment[];
  onAppointmentClick: (appointment: Appointment) => void;
}

export default function TodayAgenda({ appointments, onAppointmentClick }: TodayAgendaProps) {
  const todayAppointments = useMemo(() => {
    return appointments
      .filter((a) => isSameDay(toSalonTime(a.startTime), salonNow()) && a.status !== 'cancelled')
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [appointments]);

  const now = new Date();
  const nextUpcomingIndex = todayAppointments.findIndex((a) => isAfter(new Date(a.startTime), now));

  return (
    <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
      <div className="border-default border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Calendar className="text-accent h-4 w-4" />
          <h3 className="text-fg text-sm font-bold">Today's Agenda</h3>
          <span className="bg-muted text-fg-secondary ml-auto rounded-full px-2 py-0.5 text-xs font-medium">
            {todayAppointments.length}
          </span>
        </div>
      </div>

      <div className="max-h-[600px] overflow-y-auto">
        {todayAppointments.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Calendar className="text-fg-faint mx-auto mb-2 h-8 w-8" />
            <p className="text-fg-subtle text-sm">No appointments today</p>
          </div>
        ) : (
          <div className="divide-subtle divide-y">
            {todayAppointments.map((appointment, index) => {
              const isNext = index === nextUpcomingIndex;
              return (
                <button
                  key={appointment.id}
                  onClick={() => onAppointmentClick(appointment)}
                  className={`hover:bg-canvas w-full px-4 py-3 text-left transition-colors ${
                    isNext ? 'bg-canvas/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex flex-col items-center">
                      <span className="text-fg text-xs font-bold">
                        {format(toSalonTime(appointment.startTime), 'h:mm')}
                      </span>
                      <span className="text-fg-muted text-[10px]">
                        {format(toSalonTime(appointment.startTime), 'a')}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[appointment.status]}`}
                        />
                        <span className="text-fg truncate text-sm font-medium">
                          {appointment.customer.name}
                        </span>
                        {isNext && (
                          <span className="bg-muted text-fg shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                            Next
                          </span>
                        )}
                      </div>
                      <div className="text-fg-muted mt-0.5 flex items-center gap-2 text-xs">
                        <span className="truncate">{appointment.serviceName}</span>
                        <span className="flex shrink-0 items-center gap-0.5">
                          <Clock className="h-3 w-3" />
                          {appointment.serviceDuration}m
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
